import { Controller } from "@hotwired/stimulus"

const ZOOM_STEP = 0.5
const ZOOM_MIN = 1.0
const ZOOM_MAX = 4.0
// The point of the image that stays under the pointer: its centre.
const CENTER = 0.5

export default class extends Controller {
  static targets = ["imageContainer", "image", "zoomInButton", "zoomOutButton", "zoomLevel", "resetButton"]

  connect() {
    this.#setZoomLevel(ZOOM_MIN)
  }

  toggleZoomIn() {
    this.#setZoomLevel(this.zoomLevel + ZOOM_STEP)
  }

  toggleZoomOut() {
    this.#setZoomLevel(this.zoomLevel - ZOOM_STEP)
  }

  toggleReset() {
    this.#setZoomLevel(ZOOM_MIN)
  }

  move({ target, clientX, clientY }) {
    const imageContainerRect = this.imageContainerTarget.getBoundingClientRect()
    const imageTarget = this.#imageUnder(target)

    if (imageTarget === undefined) {
      return
    }

    if (this.zoomLevel !== ZOOM_MIN) {
      const localX = clientX - imageContainerRect.left
      const localY = clientY - imageContainerRect.top

      const xPercent = localX / imageContainerRect.width
      const yPercent = localY / imageContainerRect.height

      const top = -(yPercent - CENTER) * imageTarget.clientHeight * this.zoomLevel
      const left = -(xPercent - CENTER) * imageTarget.clientWidth * this.zoomLevel

      this.#setImagePosition(imageTarget, top, left)
    } else {
      this.#resetImagePosition(imageTarget)
    }
  }

  #setImagePosition(imageTarget, top, left) {
    imageTarget.style.position = "relative"

    const containerRect = this.imageContainerTarget.getBoundingClientRect()

    const scaledWidth = imageTarget.clientWidth * this.zoomLevel
    const scaledHeight = imageTarget.clientHeight * this.zoomLevel

    const maxLeft = Math.max(0, (scaledWidth - containerRect.width) / 2)
    const maxTop = Math.max(0, (scaledHeight - containerRect.height) / 2)

    const constrainedLeft = Math.max(-maxLeft, Math.min(maxLeft, left))
    const constrainedTop = Math.max(-maxTop, Math.min(maxTop, top))

    imageTarget.style.left = `${constrainedLeft}px`
    imageTarget.style.top = `${constrainedTop}px`
  }

  #setImagePositionDelta(imageTarget, deltaY, deltaX) {
    const currentTop = parseInt(imageTarget.style.top, 10) || 0
    const currentLeft = parseInt(imageTarget.style.left, 10) || 0
    const newY = currentTop + deltaY
    const newX = currentLeft + deltaX
    this.#setImagePosition(imageTarget, newY, newX)
  }

  #resetImagePosition(imageTarget) {
    imageTarget.style.position = "static"
    imageTarget.style.top = "0px"
    imageTarget.style.left = "0px"
  }

  // A touch pans only a zoomed image. Any other touch, on the carousel buttons, a video or the image
  // at 100%, is left to the browser, so a tap still clicks and the modal still scrolls.
  touchStart(event) {
    this.touchedImage = this.zoomLevel === ZOOM_MIN ? undefined : this.#imageUnder(event.target)

    if (this.touchedImage === undefined) {
      return
    }

    event.preventDefault()
    this.startX = event.touches[0].clientX
    this.startY = event.touches[0].clientY
  }

  touchMove(event) {
    if (this.touchedImage === undefined) {
      return
    }

    event.preventDefault()
    const deltaX = event.touches[0].clientX - this.startX
    const deltaY = event.touches[0].clientY - this.startY
    this.#setImagePositionDelta(this.touchedImage, deltaY, deltaX)

    this.startX = event.touches[0].clientX
    this.startY = event.touches[0].clientY
  }

  touchEnd(event) {
    if (this.touchedImage === undefined) {
      return
    }

    const deltaX = event.changedTouches[0].clientX - this.startX
    const deltaY = event.changedTouches[0].clientY - this.startY
    this.#setImagePositionDelta(this.touchedImage, deltaY, deltaX)
    this.touchedImage = undefined
  }

  #imageUnder(target) {
    return this.imageTargets.find(imageTarget => target.contains(imageTarget))
  }

  mouseDown(event) {
    this.move({
      currentTarget: event.currentTarget,
      target: event.target,
      clientX: event.clientX,
      clientY: event.clientY
    })
  }

  #setZoomLevel(level) {
    this.zoomLevel = Math.max(level, ZOOM_MIN)
    this.zoomLevelTarget.textContent = `${this.zoomLevel.toFixed(1) * 100}%`
    this.imageTargets.forEach(imageTarget => {
      imageTarget.style.transform = `scale(${this.zoomLevel})`

      if (this.zoomLevel !== ZOOM_MIN) {
        const currentTop = parseInt(imageTarget.style.top, 10) || 0
        const currentLeft = parseInt(imageTarget.style.left, 10) || 0
        this.#setImagePosition(imageTarget, currentTop, currentLeft)
      }
    })

    if (this.zoomLevel === ZOOM_MIN) {
      this.#disableZoomOut()
      this.imageTargets.forEach(imageTarget => {
        this.#resetImagePosition(imageTarget)
      })
    } else if (this.zoomLevel >= ZOOM_MAX) {
      this.#disableZoomIn()
    } else {
      this.#enableAllButtons()
    }

    const detail = {
      zoomLevel: this.zoomLevel
    }

    this.dispatch("zoomed", {
      detail,
      target: this.imageContainerTarget
    })
  }

  #enableAllButtons() {
    this.zoomInButtonTarget.disabled = false
    this.zoomOutButtonTarget.disabled = false
    this.resetButtonTarget.disabled = false
  }

  #disableZoomIn() {
    this.zoomInButtonTarget.disabled = true
    this.zoomOutButtonTarget.disabled = false
    this.resetButtonTarget.disabled = false
  }

  #disableZoomOut() {
    this.resetButtonTarget.disabled = true
    this.zoomOutButtonTarget.disabled = true
    this.zoomInButtonTarget.disabled = false
  }
}
